import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Column, DataGrid, DataGridRef, FilterRow, Paging, Scrolling, Selection } from 'devextreme-react/data-grid'
import { TextEditorButton, ValidationRule } from 'devextreme/common'
import { ArraySourceComposer } from '@/auth/api/connector'
import { DropDownBox, Validator } from 'devextreme-react'
import { DropDownBoxRef, DropDownBoxTypes } from 'devextreme-react/cjs/drop-down-box'
import { ValidationResult } from 'devextreme/ui/validation_group'
import { DropDownPredefinedButton } from 'devextreme/ui/drop_down_editor/ui.drop_down_editor'

interface DropDownBoxDataGridItemProps {
  object: any
  values: any
  label: string
  displayExprField: string | ((item: any) => string)
  formValidation: string
  columns: { dataField: string; caption?: string; width?: string }[]
  onChange: (value: any) => void
  readOnly?: boolean
  buttons?: (DropDownPredefinedButton | TextEditorButton)[] | undefined
  validationRules?: ValidationRule[] | undefined
}

export type IDropDownBoxDataGridItemRef = {
  validate: () => ValidationResult | undefined
}
// eslint-disable-next-line react/display-name
const DropDownBoxDataGridItem = forwardRef<IDropDownBoxDataGridItemRef, DropDownBoxDataGridItemProps>(
  (props: DropDownBoxDataGridItemProps, ref: React.ForwardedRef<IDropDownBoxDataGridItemRef>) => {
    const dropDownBoxRef = useRef<DropDownBoxRef>(null)
    const dataGridRef = useRef<DataGridRef>(null)
    const [selectedKey, setSelectedKey] = useState<string | number | null>(props.object?.id ?? null)

    useEffect(() => {
      setSelectedKey(props.object?.id ?? null)
    }, [props.object?.id])

    const onSelectionChanged = useCallback(
      (e: any) => {
        const valueFromSelection = e.selectedRowsData?.[0]
        const key = e.selectedRowKeys?.[0]
        if (key == null) return
        if (key === selectedKey) {
          dropDownBoxRef.current?.instance().close()
          return
        }
        const value = valueFromSelection ?? props.values?.find((item: any) => item?.id === key) ?? null
        if (value == null) return
        setSelectedKey(key)
        // Close dropdown immediately for better UX
        dropDownBoxRef.current?.instance().close()
        props.onChange(value)
      },
      [props, selectedKey],
    )

    const btns: (DropDownPredefinedButton | TextEditorButton)[] = []
    if (props.buttons) {
      for (const btn of props.buttons) btns.push(btn)
    }
    btns.push({ name: 'clear', location: 'after' }, { name: 'dropDown', location: 'after' })

    //metodi esposti dal componente
    useImperativeHandle(ref, () => {
      return {
        validate() {
          let validationResult: ValidationResult | undefined
          return validationResult
        },
      } satisfies IDropDownBoxDataGridItemRef
    }, [])

    // Memoize columns to prevent re-renders
    const renderedColumns = useMemo(
      () =>
        props.columns.map((column) => (
          <Column
            key={column.dataField} // Use dataField instead of index for stable keys
            dataField={column.dataField}
            caption={column.caption ?? column.dataField.toUpperCase()}
            width={column.width ?? 'auto'}
          />
        )),
      [props.columns],
    )
    const selectedRowKeys = useMemo(() => (selectedKey != null ? [selectedKey] : []), [selectedKey])
    const dropDownDataSource = useMemo(() => ArraySourceComposer('id', props.values), [props.values])

    return (
      <DropDownBox
        ref={dropDownBoxRef}
        dataSource={dropDownDataSource}
        value={selectedKey}
        valueExpr="id"
        displayExpr={props.displayExprField}
        placeholder={`Seleziona ${props.label}...`}
        onValueChanged={(e: DropDownBoxTypes.ValueChangedEvent) => {
          if (!e.event) return
          if (e.value == null && e.previousValue != null) {
            setSelectedKey(null)
            props.onChange(null)
          }
        }}
        showClearButton={true}
        dropDownOptions={{ resizeEnabled: true, width: 'auto', maxHeight: 300 }}
        readOnly={props.readOnly}
        buttons={btns}
      >
        <DataGrid
          ref={dataGridRef}
          dataSource={props.values}
          keyExpr="id"
          selectedRowKeys={selectedRowKeys}
          onSelectionChanged={onSelectionChanged}
          width={'auto'}
          height={'auto'}
          hoverStateEnabled={true}
          // Performance optimizations
          cacheEnabled={true}
          repaintChangesOnly={true}
          showBorders={false}
        >
          <Paging enabled={false} />
          <Selection mode="single" />
          <FilterRow visible={true} />
          <Scrolling mode="virtual" /> {/* Virtual scrolling for large datasets */}
          {renderedColumns}
        </DataGrid>
        <Validator validationGroup={props.formValidation} validationRules={props.validationRules} />
      </DropDownBox>
    )
  },
)

export default DropDownBoxDataGridItem
